import type { LiveKind, LiveSourceDescriptor, LiveTierName, PeerInfo, RTCIceServerLike, SendRules, SignalData } from '../../../shared/protocol.ts';
import { Observable } from './emitter.ts';
import type { LiveHealth } from './governor.ts';
import type { Signaling } from './signaling.ts';

/**
 * Live call over a full WebRTC mesh (fine for the 2–6 people of a typical recording).
 * Live media is deliberately the expendable part: local recordings carry the quality,
 * so every peer link adapts down aggressively when bandwidth or CPU gets tight.
 *
 * Each connection has four fixed transceivers — mic, camera, screen, screen audio —
 * created by the peer with the smaller id. Sources are swapped with replaceTrack, so
 * changing what you share never needs renegotiation.
 */

const ROLES = ['mic', 'camera', 'screen', 'screenAudio'] as const;
type LiveRole = (typeof ROLES)[number];
const KIND_OF: Record<LiveRole, LiveKind> = { mic: 'mic', camera: 'camera', screen: 'screen', screenAudio: 'screen' };

interface TierSpec {
  height: number;
  bitrate: number;
  fps: number;
}

const CAMERA_TIERS: Record<LiveTierName, TierSpec | null> = {
  high: { height: 720, bitrate: 1_500_000, fps: 30 },
  medium: { height: 360, bitrate: 600_000, fps: 30 },
  low: { height: 180, bitrate: 180_000, fps: 15 },
  'audio-only': null,
};
const SCREEN_TIERS: Record<LiveTierName, TierSpec | null> = {
  high: { height: 1080, bitrate: 2_000_000, fps: 15 },
  medium: { height: 720, bitrate: 900_000, fps: 8 },
  low: { height: 720, bitrate: 350_000, fps: 4 },
  'audio-only': null,
};
const TIER_ORDER: LiveTierName[] = ['high', 'medium', 'low', 'audio-only'];
const lower = (a: LiveTierName, b: LiveTierName) => (TIER_ORDER.indexOf(a) > TIER_ORDER.indexOf(b) ? a : b);

export interface RemotePeer {
  info: PeerInfo;
  pc: RTCPeerConnection;
  tracks: Partial<Record<LiveRole, MediaStreamTrack>>;
  liveSources: LiveSourceDescriptor[];
  state: RTCPeerConnectionState;
  tier: LiveTierName;
  /** Highest tier the remote asked us to send (weak receiver). */
  preferredMax: LiveTierName;
  stats: { rttMs: number | null; lossPct: number; outBps: number; inBps: number; limitation: string };
  private_: {
    lastBytesOut: number;
    lastBytesIn: number;
    lastAt: number;
    goodSince: number;
    lastChange: number;
    connectedAt: number;
    badPolls: number;
    pendingCandidates: RTCIceCandidateInit[];
  };
}

export interface LocalLive {
  mic: MediaStreamTrack | null;
  camera: MediaStreamTrack | null;
  screen: MediaStreamTrack | null;
  screenAudio: MediaStreamTrack | null;
}

export class Mesh extends Observable {
  peers = new Map<string, RemotePeer>();
  selfId = '';
  local: LocalLive = { mic: null, camera: null, screen: null, screenAudio: null };
  /**
   * Who may receive what, from the server (the host's settings). A source someone may not receive is never sent
   * to them: privacy is enforced here, at the sender, not by hiding tiles. Until the rules arrive nothing is sent.
   */
  sendRules: SendRules = {};
  /** Ceiling from this machine's own constraints (hardware tier, CPU pressure, recorder struggling). */
  localCap: LiveTierName = 'high';
  onHealth?: (h: LiveHealth) => void;
  private statsTimer: number;
  private signaling: Signaling;
  private iceServers: RTCIceServerLike[];

  constructor(signaling: Signaling, iceServers: RTCIceServerLike[]) {
    super();
    this.signaling = signaling;
    this.iceServers = iceServers;
    this.statsTimer = window.setInterval(() => void this.pollStats(), 2000);
  }

  setSelf(id: string) {
    this.selfId = id;
  }

  addPeer(info: PeerInfo) {
    if (this.peers.has(info.id) || info.id === this.selfId) return;
    // Diagnostics: localStorage "takehouse.forceRelay" = "1" sends the call only through the relay (TURN), to
    // prove it works without needing a network that blocks direct connections.
    let relayOnly = false;
    try {
      relayOnly = localStorage.getItem('takehouse.forceRelay') === '1';
    } catch {}
    if (relayOnly) console.info('[takehouse] call forced through the relay (takehouse.forceRelay)');
    const pc = new RTCPeerConnection({
      iceServers: this.iceServers as RTCIceServer[],
      bundlePolicy: 'max-bundle',
      iceTransportPolicy: relayOnly ? 'relay' : 'all',
    });
    const peer: RemotePeer = {
      info,
      pc,
      tracks: {},
      liveSources: [],
      state: 'new',
      tier: lower('medium', this.localCap),
      preferredMax: 'high',
      stats: { rttMs: null, lossPct: 0, outBps: 0, inBps: 0, limitation: 'none' },
      private_: {
        lastBytesOut: 0,
        lastBytesIn: 0,
        lastAt: 0,
        goodSince: performance.now(),
        lastChange: 0,
        connectedAt: Infinity,
        badPolls: 0,
        pendingCandidates: [],
      },
    };
    this.peers.set(info.id, peer);

    pc.onicecandidate = (e) => this.signal(info.id, { kind: 'candidate', candidate: e.candidate?.toJSON() ?? null });
    pc.onconnectionstatechange = () => {
      peer.state = pc.connectionState;
      if (pc.connectionState === 'connected' && peer.private_.connectedAt === Infinity) peer.private_.connectedAt = performance.now();
      if (pc.connectionState === 'failed' && this.isInitiator(info.id)) pc.restartIce();
      this.changed();
    };
    pc.ontrack = () => this.collectRemoteTracks(peer);
    pc.onnegotiationneeded = async () => {
      if (!this.isInitiator(info.id)) return;
      try {
        await pc.setLocalDescription();
        this.signal(info.id, { kind: 'description', description: pc.localDescription!.toJSON() });
      } catch (e) {
        console.warn('negotiation failed', e);
      }
    };

    if (this.isInitiator(info.id)) {
      for (const role of ROLES) {
        pc.addTransceiver(role === 'mic' || role === 'screenAudio' ? 'audio' : 'video', { direction: 'sendrecv' });
      }
      void this.applyLocalTracks(peer);
    }
    this.changed();
  }

  removePeer(id: string) {
    const peer = this.peers.get(id);
    if (!peer) return;
    peer.pc.close();
    this.peers.delete(id);
    this.changed();
  }

  setPeerLiveSources(id: string, sources: LiveSourceDescriptor[]) {
    const peer = this.peers.get(id);
    if (peer) {
      peer.liveSources = sources;
      this.changed();
    }
  }

  setPreferredTier(id: string, tier: LiveTierName) {
    const peer = this.peers.get(id);
    if (!peer) return;
    peer.preferredMax = tier;
    void this.applyTier(peer, lower(peer.tier, tier));
  }

  private isInitiator(peerId: string) {
    return this.selfId < peerId;
  }

  private signal(to: string, data: SignalData) {
    this.signaling.send({ type: 'signal', to, data });
  }

  async handleSignal(from: string, data: SignalData) {
    const peer = this.peers.get(from);
    if (!peer) return;
    const pc = peer.pc;
    try {
      if (data.kind === 'description') {
        await pc.setRemoteDescription(data.description as RTCSessionDescriptionInit);
        if (data.description.type === 'offer') {
          for (const t of pc.getTransceivers()) if (t.direction === 'recvonly') t.direction = 'sendrecv';
          await this.applyLocalTracks(peer);
          await pc.setLocalDescription();
          this.signal(from, { kind: 'description', description: pc.localDescription!.toJSON() });
        }
        for (const c of peer.private_.pendingCandidates.splice(0)) await pc.addIceCandidate(c).catch(() => {});
        this.collectRemoteTracks(peer);
      } else if (data.candidate) {
        if (!pc.remoteDescription) peer.private_.pendingCandidates.push(data.candidate);
        else await pc.addIceCandidate(data.candidate).catch(() => {});
      }
    } catch (e) {
      console.warn('signal handling failed', e);
    }
  }

  private transceiverFor(peer: RemotePeer, role: LiveRole) {
    // Transceivers are created in ROLES order on the initiator and appear in the same order on the answerer.
    const list = peer.pc.getTransceivers().filter((t) => t.currentDirection !== 'stopped');
    return list[ROLES.indexOf(role)];
  }

  private collectRemoteTracks(peer: RemotePeer) {
    for (const role of ROLES) {
      const t = this.transceiverFor(peer, role);
      if (t?.receiver.track) peer.tracks[role] = t.receiver.track;
    }
    this.changed();
  }

  setSendRules(rules: SendRules) {
    this.sendRules = rules;
    for (const peer of this.peers.values()) void this.applyLocalTracks(peer);
  }

  /** Point all connections at the current local tracks. */
  setLocal(local: LocalLive) {
    this.local = local;
    for (const peer of this.peers.values()) void this.applyLocalTracks(peer);
  }

  private async applyLocalTracks(peer: RemotePeer) {
    for (const role of ROLES) {
      const t = this.transceiverFor(peer, role);
      if (!t) continue;
      const allowed = this.sendRules[peer.info.id]?.[KIND_OF[role]] ?? false;
      const track = allowed ? this.local[role] : null;
      if (t.sender.track !== track) await t.sender.replaceTrack(track).catch(() => {});
    }
    await this.applyTier(peer, peer.tier, true);
  }

  setLocalCap(cap: LiveTierName) {
    if (cap === this.localCap) return;
    this.localCap = cap;
    for (const peer of this.peers.values()) void this.applyTier(peer, lower(peer.tier, cap));
  }

  private async applyTier(peer: RemotePeer, tier: LiveTierName, force = false) {
    tier = lower(lower(tier, this.localCap), peer.preferredMax);
    if (!force && tier === peer.tier) return;
    peer.tier = tier;
    peer.private_.lastChange = performance.now();
    await this.encode(peer, 'camera', CAMERA_TIERS[tier]);
    await this.encode(peer, 'screen', SCREEN_TIERS[tier]);
    this.changed();
  }

  private async encode(peer: RemotePeer, role: 'camera' | 'screen', spec: TierSpec | null) {
    const t = this.transceiverFor(peer, role);
    if (!t) return;
    const params = t.sender.getParameters();
    if (!params.encodings?.length) return; // not negotiated yet; applied again once it is
    const enc = params.encodings[0];
    const track = t.sender.track;
    enc.active = !!spec && !!track;
    if (spec && track) {
      const h = track.getSettings().height ?? spec.height;
      enc.scaleResolutionDownBy = Math.max(1, h / spec.height);
      enc.maxBitrate = spec.bitrate;
      enc.maxFramerate = spec.fps;
    }
    (params as any).degradationPreference = role === 'screen' ? 'maintain-resolution' : 'balanced';
    await t.sender.setParameters(params).catch((e) => console.warn('setParameters', e));
  }

  private async pollStats() {
    const agg: LiveHealth = { peers: 0, rttMs: null, lossPct: 0, bandwidthLimited: false, availableOutgoingBps: null, liveSendBps: 0 };
    for (const peer of this.peers.values()) {
      if (peer.pc.connectionState !== 'connected') continue;
      agg.peers++;
      let report: RTCStatsReport;
      try {
        report = await peer.pc.getStats();
      } catch {
        continue;
      }
      let bytesOut = 0;
      let bytesIn = 0;
      let rtt: number | null = null;
      let lost = 0;
      let limitation = 'none';
      let available: number | null = null;
      report.forEach((s: any) => {
        if (s.type === 'outbound-rtp') {
          bytesOut += s.bytesSent ?? 0;
          if (s.kind === 'video' && s.qualityLimitationReason && s.qualityLimitationReason !== 'none') limitation = s.qualityLimitationReason;
        } else if (s.type === 'inbound-rtp') {
          bytesIn += s.bytesReceived ?? 0;
        } else if (s.type === 'remote-inbound-rtp') {
          if (typeof s.fractionLost === 'number') lost = Math.max(lost, s.fractionLost * 100);
        } else if (s.type === 'candidate-pair' && (s.nominated || s.selected) && s.state === 'succeeded') {
          if (typeof s.currentRoundTripTime === 'number') rtt = s.currentRoundTripTime * 1000;
          if (typeof s.availableOutgoingBitrate === 'number') available = s.availableOutgoingBitrate;
        }
      });
      const now = performance.now();
      const p = peer.private_;
      const dt = p.lastAt ? (now - p.lastAt) / 1000 : 0;
      peer.stats = {
        rttMs: rtt,
        lossPct: lost,
        outBps: dt ? ((bytesOut - p.lastBytesOut) * 8) / dt : 0,
        inBps: dt ? ((bytesIn - p.lastBytesIn) * 8) / dt : 0,
        limitation,
      };
      p.lastBytesOut = bytesOut;
      p.lastBytesIn = bytesIn;
      p.lastAt = now;

      agg.rttMs = Math.max(agg.rttMs ?? 0, rtt ?? 0) || agg.rttMs;
      agg.lossPct = Math.max(agg.lossPct, lost);
      // A new link reports 'bandwidth' while its estimate ramps up; that's not congestion.
      agg.bandwidthLimited ||= limitation === 'bandwidth' && now - p.connectedAt > 12000;
      if (available != null) agg.availableOutgoingBps = Math.min(agg.availableOutgoingBps ?? Infinity, available);
      agg.liveSendBps += peer.stats.outBps;

      this.adapt(peer, available);
    }
    this.onHealth?.(agg);
    this.changed();
  }

  /** Step a link down fast on trouble, back up slowly once it has been clean for a while. */
  private adapt(peer: RemotePeer, available: number | null) {
    const now = performance.now();
    const p = peer.private_;
    const cur = TIER_ORDER.indexOf(peer.tier);
    const spec = CAMERA_TIERS[peer.tier];
    // Bandwidth estimates start low and ramp up; give a new link time before trusting them.
    const warmedUp = now - p.connectedAt > 12000;
    const trouble =
      peer.stats.limitation === 'cpu' ||
      peer.stats.lossPct > 5 ||
      (warmedUp && peer.stats.limitation === 'bandwidth') ||
      (warmedUp && spec != null && available != null && available < spec.bitrate * 0.6);
    // Two bad readings in a row (~4 s) before stepping down: single stats samples are noisy.
    p.badPolls = trouble ? p.badPolls + 1 : 0;
    if (trouble) {
      p.goodSince = now;
      if (p.badPolls >= 2 && now - p.lastChange > 4000 && cur < TIER_ORDER.length - 1) void this.applyTier(peer, TIER_ORDER[cur + 1]);
      return;
    }
    if (cur > 0 && now - p.goodSince > 15000 && now - p.lastChange > 15000) {
      // The estimate rarely probes far above what we already send, so "comfortably at current" is the signal.
      if (!spec || available == null || available > spec.bitrate * 0.9) void this.applyTier(peer, TIER_ORDER[cur - 1]);
    }
  }

  close() {
    clearInterval(this.statsTimer);
    for (const p of this.peers.values()) p.pc.close();
    this.peers.clear();
  }
}
